import { useState } from 'react';

import { checkOngoingExport } from '../services/api/requests/asyncExportRequests';
import { AsyncJob, AsyncJobProcessTypeEnum } from '../types/asyncExport.types';
import { useAsyncExport } from './useAsyncExport';

interface UseAsyncExportFlowParams {
	processType: AsyncJobProcessTypeEnum;
	cycleId?: number;
	companyAreaId?: number;
	collaborationType?: 'planning' | 'marketing' | 'commercial';
	onSuccess?: () => void;
	onExportStarted?: () => void;
}

interface UseAsyncExportFlowReturn {
	handleExportClick: () => Promise<void>;
	isLoading: boolean;
	showConfirmationModal: boolean;
	showOngoingModal: boolean;
	ongoingJob: AsyncJob | null;
	handleConfirmation: () => Promise<void>;
	handleConfirmationCancel: () => void;
	handleOngoingConfirm: () => Promise<void>;
	handleOngoingCancel: () => void;
}

export const useAsyncExportFlow = (
	params: UseAsyncExportFlowParams
): UseAsyncExportFlowReturn => {
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [showOngoingModal, setShowOngoingModal] = useState(false);
	const [ongoingJob, setOngoingJob] = useState<AsyncJob | null>(null);

	const { handleExport, isLoading } = useAsyncExport({
		processType: params.processType,
		cycleId: params.cycleId,
		companyAreaId: params.companyAreaId,
		collaborationType: params.collaborationType,
		onExportStarted: params.onExportStarted,
	});

	const handleExportClick = async (): Promise<void> => {
		try {
			// 1. Verificar se há exportação em andamento
			const checkResponse = await checkOngoingExport({
				process_type: params.processType,
				cycle_id: params.cycleId,
			});

			if (checkResponse.has_ongoing && checkResponse.job) {
				// Exibir modal de exportação em andamento
				setOngoingJob(checkResponse.job);
				setShowOngoingModal(true);
				return;
			}

			// 2. Se não houver exportação em andamento, exibir modal de confirmação
			setShowConfirmationModal(true);
		} catch (error: unknown) {
			// Erro na verificação - já tratado no useAsyncExport ou exibir erro genérico
			console.error('Error checking ongoing export:', error);
		}
	};

	const handleConfirmation = async (): Promise<void> => {
		setShowConfirmationModal(false);
		try {
			await handleExport();
			if (params.onSuccess) {
				params.onSuccess();
			}
		} catch (error: unknown) {
			// Erros já tratados no useAsyncExport
			console.error('Error in export:', error);
		}
	};

	const handleConfirmationCancel = (): void => {
		setShowConfirmationModal(false);
	};

	const handleOngoingConfirm = async (): Promise<void> => {
		setShowOngoingModal(false);
		setOngoingJob(null);
		try {
			await handleExport();
			if (params.onSuccess) {
				params.onSuccess();
			}
		} catch (error: unknown) {
			// Erros já tratados no useAsyncExport
			console.error('Error forcing export:', error);
		}
	};

	const handleOngoingCancel = (): void => {
		setShowOngoingModal(false);
		setOngoingJob(null);
	};

	return {
		handleExportClick,
		isLoading,
		showConfirmationModal,
		showOngoingModal,
		ongoingJob,
		handleConfirmation,
		handleConfirmationCancel,
		handleOngoingConfirm,
		handleOngoingCancel,
	};
};
